"use client";
import * as React from "react";
import { useState, useEffect } from "react";
import { CheckCircle, Star, Award, Zap, Shield, Globe } from "lucide-react";

const BenefitCard = ({ icon: Icon, title, description, color }) => {
  return (
    <div className="group relative bg-white/60 backdrop-blur-md border border-white/40 rounded-2xl p-6 hover:bg-white/80 transition-all duration-300 hover:scale-105 hover:shadow-xl">
      <div className="flex items-start space-x-4">
        <div className={`p-3 rounded-xl ${color} shadow-lg group-hover:scale-110 transition-transform duration-300`}>
          <Icon className="w-6 h-6 text-white" />
        </div>
        <div className="flex-1">
          <h3 className="text-lg font-bold text-[#1E293B] mb-2">{title}</h3>
          <p className="text-[#64748B] text-sm leading-relaxed">{description}</p>
        </div>
      </div>
    </div>
  );
};

const FeatureHighlight = ({ title, description }) => {
  return (
    <div className="flex items-start space-x-3 mb-4">
      <CheckCircle className="w-5 h-5 text-[#1719A8] mt-1 flex-shrink-0" />
      <div>
        <h4 className="font-semibold text-[#1E293B] mb-1">{title}</h4>
        <p className="text-[#64748B] text-sm leading-relaxed">{description}</p>
      </div>
    </div>
  );
};

function ProductDescription() {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);

  const benefits = [
    {
      icon: Star,
      title: "Surprisingly Affordable",
      description: "Cost-effective solution with unique features rarely seen in other software of this kind available in the market.",
      color: "bg-gradient-to-br from-[#D96F2B] to-[#C9C51A]"
    },
    {
      icon: Award,
      title: "Industry Best Practices",
      description: "Built on latest technologies and standards, based on industry best practices with solid business logic.",
      color: "bg-gradient-to-br from-[#1719A8] to-[#3B82F6]"
    },
    {
      icon: Zap,
      title: "User-Friendly Design",
      description: "Incredibly easy to use and as simple as end-user wants, or as sophisticated as business needs require.",
      color: "bg-gradient-to-br from-[#C9C51A] to-[#10B981]"
    },
    {
      icon: Shield,
      title: "Cloud-Based Security",
      description: "Secure and scalable cloud-based solution without limitations, ensuring data protection and reliability.",
      color: "bg-gradient-to-br from-[#8B5CF6] to-[#1719A8]"
    },
    {
      icon: Globe,
      title: "International Standards",
      description: "Compliant with international standards and can be implemented in any airport with local administrative norms.",
      color: "bg-gradient-to-br from-[#EF4444] to-[#D96F2B]"
    }
  ];

  const keyFeatures = [
    {
      title: "Comprehensive Solution",
      description: "Next-generation, ready-to-use solution for individual end users with complete luggage management capabilities."
    },
    {
      title: "Latest Technologies",
      description: "Built on cutting-edge technologies and standards with solid business logic and industry best practices."
    },
    {
      title: "Client Customizations",
      description: "Adaptable to all kinds of organizations with client-level customizations for specific business needs."
    },
    {
      title: "Multi-Location Support",
      description: "Applicable for single unit organizations and organization units at multiple locations across the USA."
    },
    {
      title: "Business Intelligence",
      description: "Capable of handling all types of Business Intelligence with multiple BI reports, statistics, and graphs."
    },
    {
      title: "Airport Collaboration",
      description: "Facilitates collaboration between airports and airlines in luggage movement with care, security, and comfort."
    }
  ];

  return (
    <section className="relative py-20 overflow-hidden bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/40" aria-labelledby="product-description-heading">
      {/* Background decorative elements */}
      <div className="absolute inset-0 overflow-hidden" aria-hidden="true">
        <div className="absolute top-1/4 left-1/4 w-72 h-72 lg:w-96 lg:h-96 bg-blue-200/20 rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/4 right-1/4 w-72 h-72 lg:w-96 lg:h-96 bg-indigo-200/20 rounded-full blur-3xl"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-orange-200/15 rounded-full blur-2xl"></div>
      </div>

      <div className="container max-w-7xl mx-auto px-4 lg:px-6 relative">
        {/* Section Header */}
        <header className="text-center mb-16">
          <div className="inline-flex items-center px-6 py-2 bg-[#1719A8]/90 backdrop-blur-md border border-[#1719A8]/60 rounded-full mb-6 shadow-lg">
            <span className="w-2 h-2 bg-white rounded-full mr-3 animate-pulse"></span>
            <span className="text-sm text-white font-semibold uppercase tracking-wider">
              Product Overview
            </span>
          </div>
          <h1 id="product-description-heading" className="text-4xl lg:text-6xl font-bold text-slate-900 mb-3 leading-tight">
            About BaggyBeach
            <span className="block leading-normal bg-gradient-to-r from-indigo-600 to-blue-600 bg-clip-text text-transparent">
              Luggage Management System
            </span>
          </h1>
          <p className="text-xl text-slate-600 max-w-4xl mx-auto leading-relaxed">
            Baggage Tracking is a program that aims to improve efficiencies in baggage handling operations to meet
            the changing demands of passengers as the air transport industry is set to double in size by 2035.
          </p>
        </header>

        <div className={`transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>

          {/* Main Description */}
          <div className="grid lg:grid-cols-2 gap-12 mb-16">
            <div className="space-y-6">
              <div className="bg-white/60 backdrop-blur-md border border-white/40 rounded-3xl p-8 shadow-xl">
                <h2 className="text-2xl font-bold text-[#1E293B] mb-6">Future-Ready Solution</h2>
                <p className="text-[#64748B] leading-relaxed mb-4">
                  Baggage Tracking is a program that aims to improve efficiencies in baggage handling operations to meet
                  the changing demands of passengers as the air transport industry is set to double in size by 2035.
                </p>
                <p className="text-[#64748B] leading-relaxed">
                  This software is surprisingly affordable with many unique features that are rarely seen in other
                  software of this kind available in the market, making it accessible for airports of all sizes.
                </p>
              </div>

              <div className="bg-white/60 backdrop-blur-md border border-white/40 rounded-3xl p-8 shadow-xl">
                <h2 className="text-2xl font-bold text-[#1E293B] mb-6">Technical Excellence</h2>
                <p className="text-[#64748B] leading-relaxed mb-4">
                  BaBe Software is built on the latest technologies and standards, based on industry best practices
                  with solid business logic. The system is designed to be user-friendly and incredibly easy to use.
                </p>
                <p className="text-[#64748B] leading-relaxed">
                  As a cloud-based, secure, and scalable solution without limitations, BaBe can be implemented in
                  any airport and third-party airline companies in the USA with their local administrative norms.
                </p>
              </div>
            </div>

            <div className="space-y-6">
              <div className="bg-white/60 backdrop-blur-md border border-white/40 rounded-3xl p-8 shadow-xl">
                <h2 className="text-2xl font-bold text-[#1E293B] mb-6">Key Features</h2>
                <div className="space-y-4">
                  {keyFeatures.map((feature, index) => (
                    <FeatureHighlight
                      key={index}
                      title={feature.title}
                      description={feature.description}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Benefits Grid */}
          <div className="mb-16">
            <h2 className="text-3xl font-bold text-center text-[#1E293B] mb-12">
              Why Choose BaBe?
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {benefits.map((benefit, index) => (
                <BenefitCard
                  key={index}
                  icon={benefit.icon}
                  title={benefit.title}
                  description={benefit.description}
                  color={benefit.color}
                />
              ))}
            </div>
          </div>

          {/* Impact Section */}
          <div className="bg-gradient-to-br from-[#1719A8] to-[#0F1280] rounded-3xl p-8 lg:p-12 text-white">
            <div className="grid lg:grid-cols-2 gap-8 items-center">
              <div>
                <h2 className="text-3xl font-bold mb-6">Operational Impact</h2>
                <div className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="w-5 h-5 text-[#C9C51A] mt-1 flex-shrink-0" />
                    <p className="text-white/90">Decreases luggage loss incidents significantly</p>
                  </div>
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="w-5 h-5 text-[#C9C51A] mt-1 flex-shrink-0" />
                    <p className="text-white/90">Reduces passenger waiting lines and complaints</p>
                  </div>
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="w-5 h-5 text-[#C9C51A] mt-1 flex-shrink-0" />
                    <p className="text-white/90">Provides clear luggage transportation guidelines for airport authorities</p>
                  </div>
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="w-5 h-5 text-[#C9C51A] mt-1 flex-shrink-0" />
                    <p className="text-white/90">Resolves airline weightage issues efficiently</p>
                  </div>
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="w-5 h-5 text-[#C9C51A] mt-1 flex-shrink-0" />
                    <p className="text-white/90">Ensures timely luggage delivery to desired destinations</p>
                  </div>
                </div>
              </div>
              <div className="text-center lg:text-right">
                <div className="inline-block bg-white/10 backdrop-blur-md rounded-2xl p-1 md:p-4 border border-white/20">
                  <img
                      src="https://cdn.acsdev.in/Vasta/product4.png"
                      alt="BaBe Benifits"
                      className="w-full h-[150px] md:h-full rounded-lg shadow-2xl border border-white/20 backdrop-blur-sm"
                    />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default ProductDescription;
